// Small shared primitives: chips, form fields, star rating, spinners, errors.
// Built on the shadcn/ui components in ./ui/ — prefer using those directly for
// anything they cover; these exist for app-specific composites and to give
// native form elements the same look.
import { Fragment } from "react";
import type { ReactNode, SelectHTMLAttributes } from "react";
import { Star, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ACTIVITY_ICONS, type Activity } from "../lib/format";
import { useT } from "../lib/i18n";

/**
 * Inline "[icon] Hiking · [icon] Ski touring" run. Icons are components now, so this
 * can't be a plain string join — every call site that used to `.join(" · ")`
 * renders through here instead.
 */
export function ActivityList({ activities, className }: { activities: Activity[]; className?: string }) {
  const { labelizeT } = useT();
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-1 gap-y-0.5", className)}>
      {activities.map((a, i) => {
        const Icon = ACTIVITY_ICONS[a];
        return (
          <Fragment key={a}>
            {i > 0 && <span aria-hidden>·</span>}
            {Icon && <Icon aria-hidden className="size-3.5 shrink-0" />}
            {labelizeT(a)}
          </Fragment>
        );
      })}
    </span>
  );
}

/** Toggleable filter pill. Active state fills with the brand primary. */
export function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-150 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 active:scale-[0.97]",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-card"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-foreground">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-sm text-destructive">{error}</span>}
    </label>
  );
}

export const TextInput = Input;
export const TextArea = Textarea;

/** Native <select> styled to match the shadcn Input. Use the composed
 *  ./ui/select.tsx for new work; this keeps plain-option call sites simple. */
export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        "h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
        className,
      )}
    />
  );
}

export function Stars({ rating, onChange }: { rating: number | null; onChange?: (r: number | null) => void }) {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-0.5" role={onChange ? "radiogroup" : undefined}>
      {[1, 2, 3, 4, 5].map((n) =>
        onChange ? (
          <button
            key={n}
            type="button"
            aria-label={t(n === 1 ? "stars.one" : "stars.other", { count: n })}
            onClick={() => onChange(rating === n ? null : n)}
            className={cn(
              "leading-none transition-transform duration-150 hover:scale-110",
              rating != null && n <= rating ? "text-amber-400" : "text-muted-foreground/30",
            )}
          >
            <Star aria-hidden className="size-5" fill="currentColor" />
          </button>
        ) : (
          <span
            key={n}
            className={cn("leading-none", rating != null && n <= rating ? "text-amber-400" : "text-muted-foreground/30")}
          >
            <Star aria-hidden className="size-3.5" fill="currentColor" />
          </span>
        ),
      )}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useT();
  return (
    <div className="flex items-center justify-center gap-2.5 p-10 text-sm text-muted-foreground">
      <span className="size-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
      {label ?? t("common.loading")}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Alert variant="destructive" className="m-4 w-auto">
      <TriangleAlert aria-hidden className="size-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed bg-card/60 p-10 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}
